import React from "react";
import { createPortal } from "react-dom";
import { Box, Tooltip } from "@mui/material";
import { Draggable } from "@hello-pangea/dnd";
import {
  PrecisionManufacturing as MachineIcon,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";

interface SidebarMachineRowProps {
  machineId: string;
  modelName: string;
  serialNumber: string;
  thumbnailUrl: string | null;
  draggableIndex: number;
  categoryKey: string;
}

const SidebarMachineRow: React.FC<SidebarMachineRowProps> = ({
  machineId,
  modelName,
  serialNumber,
  thumbnailUrl,
  draggableIndex,
}) => {
  return (
    <Draggable draggableId={`machine::${machineId}`} index={draggableIndex}>
      {(provided, snapshot) => {
        const draggableElement = (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            sx={{
              padding: "5px 8px 5px 16px", /* spec: machine row padding, indented */
              display: "flex",
              alignItems: "center",
              gap: "8px", /* spec: gap between thumbnail and text */
              boxSizing: "border-box",
              cursor: snapshot.isDragging ? "grabbing" : "grab",
              transition: snapshot.isDragging ? "none" : "background-color 0.15s ease",
              transform: snapshot.isDragging ? "rotate(2deg)" : "none",
              boxShadow: snapshot.isDragging
                ? "0 8px 24px rgba(0,0,0,0.15)"
                : "none",
              opacity: snapshot.isDragging ? 0.92 : 1,
              backgroundColor: snapshot.isDragging ? "#FFFFFF" : "transparent",
              borderRadius: "6px",
              zIndex: snapshot.isDragging ? 9999 : undefined,
              "&:hover": snapshot.isDragging
                ? {}
                : {
                    backgroundColor: "#F7F5F0", /* spec: input-bg hover */
                  },
            }}
          >
            {/* 24x24 thumbnail — spec: 24px square, 4px radius, #EDEAE4 placeholder */}
            {thumbnailUrl ? (
              <Box
                component="img"
                src={thumbnailUrl}
                alt={modelName}
                sx={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "4px",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "4px",
                  backgroundColor: "#EDEAE4", /* spec: placeholder bg */
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <MachineIcon sx={{ fontSize: 14, color: "#8C9099" /* spec: text-muted */ }} />
              </Box>
            )}

            {/* Name + SN stacked — per mockup layout */}
            <Tooltip title={`${modelName} (SN: ${serialNumber})`} enterDelay={600}>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                {/* Machine name — spec: Sora 12px/500 text-primary */}
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#0C1117", /* spec: text-primary */
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: "16px",
                  }}
                >
                  {modelName}
                </Box>
                {/* Serial number — spec: JetBrains Mono 9px/400 text-muted */}
                {serialNumber && (
                  <Box
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "9px",
                      fontWeight: 400,
                      color: "#8C9099", /* spec: text-muted */
                      lineHeight: "12px",
                    }}
                  >
                    SN:{serialNumber}
                  </Box>
                )}
              </Box>
            </Tooltip>
          </Box>
        );

        // Portal fix: render dragging element in document.body to escape
        // parent transforms, overflow, and position contexts
        if (snapshot.isDragging) {
          return createPortal(draggableElement, document.body);
        }

        return draggableElement;
      }}
    </Draggable>
  );
};

export default SidebarMachineRow;
